import { Injectable, Injector } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { tap, map, delay } from 'rxjs/operators';
import { SelectItem } from 'primeng/api';
import { AppConsts } from '@shared/AppConsts';
import { ApplicationService } from '@app/shared/application/application.service';
import { EhcDto, EhcServiceProxy, EhcStatus, GetEhcOutput, EhcSelfEmploymentServiceProxy, CreateOrUpdateEhcSelfEmploymentInput, EhcSelfEmploymentDto, EhcMedicalFieldDto, CreateOrUpdateEhcMedicalFieldInput, EhcMedicalFieldServiceProxy, MedRegWbtDto, EhcLanguageSkillServiceProxy, EhcPracticeServiceProxy, EhcElectronicPatientRecordServiceProxy, EhcContactServiceProxy, EhcInvoiceAddressServiceProxy, EhcLanguageSkillDto, CreateOrUpdateEhcLanguageSkillInput, EhcDocumentsOutput, EhcPracticeDto, CreateOrUpdateEhcPracticesInput, CreateOrUpdateEhcPracticesOutput, EhcElectronicPatientRecordDto, CreateOrUpdateEhcElectronicPatientRecordInput, EhcContactDto, CreateOrUpdateEhcContactInput, CreateOrUpdateEhcEmploymentsInput, EhcPostgraduateTitleDto, EhcEmploymentOutput, EhcInvoiceAddressDto, EhcEmploymentDto, LiaTypeServiceProxy, ExternalSystemEnum, EhcDocumentServiceProxy, EhcStaffDto, CreateOrUpdateEhcStaffInput, CreateOrUpdateEhcPostgraduateTitlesInput, EhcPostgraduateTitleServiceProxy, CreateOrUpdateEhcPostgraduateTitlesOutput, GetEhcPersonalLicenceDto, CreateOrUpdateEhcInvoiceAddressInput, ApplicationUploadFilesInput, GeneralFileInfoDto, GeneralFileDto, EhcStaffServiceProxy, GeneralDocumentGroupDto, LiaContactDto, CreateOrUpdateEhcRegisterOutputDto, CreateOrUpdateEhcRegister, EhcRegisterDto, EhcRegisterServiceProxy, CreateOrUpdateEhcQualityRequirementInput, EhcQualityRequirementServiceProxy, EhcQualityRequirementDto, EhcEmploymentOrPostgraduateTitleDto, EhcEmploymentOrPostgraduateTitleServiceProxy, CreateOrUpdateEhcEmploymentOrPostgraduateTitlesOutput, CreateOrUpdateEhcEmploymentOrPostgraduateTitleInput, ContactDto, ApplicationStatus, PersonApplicationSummaryDto, MySironaPersonContactDto } from './../../../shared/service-proxies/service-proxies';
import { AppComponentBase } from '@shared/common/app-component-base';

@Injectable({providedIn: 'root'})
export class EhcService extends AppComponentBase {

    ehc: EhcDto;
    ehcResultDocuments: GeneralFileInfoDto[];
    private ehcsForPersonSubject = new ReplaySubject<PersonApplicationSummaryDto[]>(1);
    readonly ehcsForPerson$ = this.ehcsForPersonSubject.asObservable();

    constructor(
        injector: Injector,
        private applicationService: ApplicationService,
        private ehcService: EhcServiceProxy,
        private ehcSelfEmploymentService: EhcSelfEmploymentServiceProxy,
        private ehcMedicalFieldService: EhcMedicalFieldServiceProxy,
        private ehcLanguageSkillService: EhcLanguageSkillServiceProxy,
        private ehcPracticeService: EhcPracticeServiceProxy,
        private ehcElectronicPatientRecordService: EhcElectronicPatientRecordServiceProxy,
        private ehcStaffService: EhcStaffServiceProxy,
        private ehcPostgraduateTitleService: EhcPostgraduateTitleServiceProxy,
        private ehcDocumentService:  EhcDocumentServiceProxy,
        private ehcContactService:  EhcContactServiceProxy,
        private ehcInvoiceAddressService: EhcInvoiceAddressServiceProxy,
        private ehcRegisterService: EhcRegisterServiceProxy,
        private ehcQualityRequirementService: EhcQualityRequirementServiceProxy,
        private liaTypeService: LiaTypeServiceProxy,
        private ehcEmploymentOrPostgraduateTitleService: EhcEmploymentOrPostgraduateTitleServiceProxy
    ) {
        super(injector);
    }

    loadEhcsForPerson(gln: string): Observable<PersonApplicationSummaryDto[]> {
        if (gln && this.isGranted('Pages.Authority.Applications.Person')) {
            return this.ehcService.getEhcsForPerson(gln).pipe(
                tap(ehcs => this.ehcsForPersonSubject.next(ehcs))
            );
            } else {
            this.ehcsForPersonSubject.next([]);
            return of([]);
        }
    }

    getExternalSystemByProfessionId(professionId: string): Observable<ExternalSystemEnum> {
        return this.liaTypeService.getExternalRegisterByProfessionId(professionId);
    }

    getEhc(caseId: string): Observable<GetEhcOutput> {
        this.ehc = null;
        return this.ehcService.getEhc(caseId).pipe(
            tap((output: GetEhcOutput) => {
                this.ehc = output.ehc;
            })
        );
    }

    getFullName(): Observable<string> {
        return this.ehcService.getName(this.ehc.application.caseId).pipe(
            map(nameTuple => nameTuple?.item1 ?? '')
        );
    }

    closeEhc(): void {
        this.ehc = null;
    }

    getMyEhcs(): Observable<PersonApplicationSummaryDto[]> {
        return this.ehcService.getMyEhcs();
    }

    getEhcPersonalLicenceForEhc(): Observable<GetEhcPersonalLicenceDto> {
        return this.ehcService.getExpenseHealthCarePersonalLicenceForEhc(this.caseId);
    }

    get caseId(): string {
        return this.ehc.application.caseId;
    }

    get status(): ApplicationStatus {
        return this.ehc.application.status;
    }

    get profession(): string {
        return this.ehc?.professionId;
    }

    get liaCaseId(): string {
        return this.ehc.liaCaseId;
    }

    get firstStep(): EhcStatus {
        return this.ehc.firstStep;
    }

    get currentStep(): EhcStatus {
        return this.ehc.currentStep;
    }

    get medRegId(): number {
        return this.ehc.ehcRegister.medRegId;
    }

    get hasMedRegId(): boolean {
        return this.ehc.ehcRegister?.medRegId != null;
    }

    get psyRegId(): number {
        return this.ehc.ehcRegister.psyRegId;
    }

    get hasPsyRegId(): boolean {
        return this.ehc.ehcRegister?.psyRegId != null;
    }

    get naregId(): number {
        return this.ehc.ehcRegister.naregId;
    }

    get hasNaregId(): boolean {
        return this.ehc.ehcRegister?.naregId != null;
    }

    get hasLiaApplicationInStatusWaiting(): boolean {
        return this.ehc.hasLiaApplicationInWaitingStatus;
    }

    checkDocuments(): Observable<EhcStatus[]> {
        return this.ehcService.validateThatAllMandatoryDocumentsHaveBeenUploaded(this.caseId);
    }

    checkEhcPracticeDocuments() {
        const documents = this.ehc.ehcPractice.ehcPracticeRecordDocuments;
        if (documents == null) {
            return false;
        }
        const practiceRecordCount = this.ehc.ehcPractice.ehcPracticeRecords.length;
        const fileGroup = documents.filter((document) => document.typeId === AppConsts.codes.documentType.proofOfPractice);
        const proofOfPracticeDocuments = fileGroup[0].files;
        return proofOfPracticeDocuments.length >= practiceRecordCount;
    }

    getEvaluationComments(): Observable<string> {
        return this.ehcService.getEvaluationComments(this.caseId);
    }

    getNextStatus(status: EhcStatus): Observable<EhcStatus> {
        return this.ehcService.getNextStatus(this.caseId, status);
    }

    getSelfEmployment(): Observable<EhcSelfEmploymentDto> {
        return of(this.ehc.ehcSelfEmployment).pipe(delay(0));
    }

    getPostgraduateTitles(): Observable<SelectItem[]> {
        return this.ehcMedicalFieldService.getPostgraduateTitles(this.caseId).pipe(
          map((items: MedRegWbtDto[]) => items.map((item: MedRegWbtDto) => ({
                label: item.art.sironaId,
                value: item.art.sironaId
              })))
        );
    }

    createOrUpdateEhcSelfEmployment(input: CreateOrUpdateEhcSelfEmploymentInput): Observable<void> {
        this.applicationService.setStepToReview(EhcStatus.SelfEmployment);
        return this.ehcSelfEmploymentService.createOrUpdateEhcSelfEmployment(input).pipe(tap((status: EhcStatus) => {
            this.ehc.ehcSelfEmployment = input.ehcSelfEmployment;
            this.ehc.currentStep = status;
        })).pipe(map((status: EhcStatus) => null));
    }

    getEhcMedicalField(): Observable<EhcMedicalFieldDto>{
        return of(this.ehc.ehcMedicalField).pipe(delay(0));
    }

    createOrUpdateEhcMedicalField(input: CreateOrUpdateEhcMedicalFieldInput): Observable<void> {
        this.applicationService.setStepToReview(EhcStatus.MedicalField);
        return this.ehcMedicalFieldService.createOrUpdateEhcMedicalField(input).pipe(tap((status: EhcStatus) => {
            this.ehc.ehcMedicalField = input.ehcMedicalField;
            this.ehc.currentStep = status;
        })).pipe(map((status: EhcStatus) => null));
    }

    getEhcPostgraduateTitles(): Observable<EhcPostgraduateTitleDto[]>{
        return this.ehcPostgraduateTitleService.getList(this.caseId);
    }

    createOrUpdateEhcPostgraduateTitles(input: CreateOrUpdateEhcPostgraduateTitlesInput): Observable<void> {
        this.applicationService.setStepToReview(EhcStatus.PostgraduateTitle);
        return this.ehcPostgraduateTitleService.createOrUpdateEhcPostgraduateTitles(input).pipe(tap((output: CreateOrUpdateEhcPostgraduateTitlesOutput) => {
            this.ehc.ehcPostgraduateTitles = output.ehcPostgraduateTitles;
            this.ehc.currentStep = output.status;
        })).pipe(map((output: CreateOrUpdateEhcPostgraduateTitlesOutput) => null));
    }

    getEhcLanguageSkill(): Observable<EhcLanguageSkillDto> {
        return of(this.ehc.ehcLanguageSkill).pipe(delay(0));
    }

    createOrUpdateEhcLanguageSkill(input: CreateOrUpdateEhcLanguageSkillInput): Observable<void> {
        this.applicationService.setStepToReview(EhcStatus.LanguageSkill);
        return this.ehcLanguageSkillService.createOrUpdateEhcLanguageSkill(input).pipe(tap((status: EhcStatus) => {
            this.ehc.ehcLanguageSkill = input.ehcLanguageSkill;
            this.ehc.currentStep = status;
        })).pipe(map((status: EhcStatus) => null));
    }

    getLanguageSkillFiles(forceRefresh?: boolean, caseId = this.caseId): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.ehcLanguageSkillService
                .getEhcLanguageSkillDocuments(caseId, EhcStatus.LanguageSkill)
                .pipe(
                    tap((output: EhcDocumentsOutput) => {
                        this.ehc.ehcLanguageSkill.languageSkillDocuments = output.ehcDocuments;
                    })
                )
                .pipe(map((output: EhcDocumentsOutput) => output.ehcDocuments));
        }
        return of(this.ehc.ehcLanguageSkill.languageSkillDocuments);
    }

    getLanguageSkillFile(fileId: number): Observable<GeneralFileDto> {
        return this.ehcLanguageSkillService.getFile(this.caseId, EhcStatus.LanguageSkill, fileId);
    }

    uploadLanguageSkillFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.ehcLanguageSkillService.uploadFiles(input);
    }

    deleteLanguageSkillFile(fileId: number): Observable<void> {
        return this.ehcLanguageSkillService.deleteFile(this.caseId, EhcStatus.LanguageSkill, fileId);
    }

    getEhcPractice(): Observable<EhcPracticeDto> {
        return this.ehcPracticeService.getEhcPractice(this.caseId);
    }

    createBlankEhcPracticeRecord(postgraduateTitleId: string = undefined): Observable<string> {
        return this.ehcPracticeService.createBlankEhcPracticeRecord(this.caseId, postgraduateTitleId);
    }

    createOrUpdateEhcPractices(input: CreateOrUpdateEhcPracticesInput): Observable<void> {
        this.applicationService.setStepToReview(EhcStatus.Practice);
        return this.ehcPracticeService.createOrUpdateEhcPractices(input).pipe(tap((output: CreateOrUpdateEhcPracticesOutput) => {
            this.ehc.ehcPractice = input.ehcPractice;
            this.ehc.ehcPractice.ehcPracticeConfigSnapshot = output.ehcPractice.ehcPracticeConfigSnapshot;
            this.ehc.currentStep = output.status;
        })).pipe(map((output: CreateOrUpdateEhcPracticesOutput) => null));
    }

    deleteEhcPracticeRecord(guid: string): Observable<void> {
        return this.ehcPracticeService.deleteEhcPracticeRecord(this.caseId, guid);
    }

    getEhcPracticeRecordFiles(
        forceRefresh?: boolean,
        caseId = this.caseId,
        hasExemptionAccordingArt37Kvg: boolean | undefined = undefined
    ): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            if (
                this.ehc.ehcPractice.exemptionAccordingArt37Kvg === true &&
                hasExemptionAccordingArt37Kvg === undefined
            ) {
                hasExemptionAccordingArt37Kvg = true;
            }
            return this.ehcPracticeService
                .getEhcPracticeRecordFiles(caseId, hasExemptionAccordingArt37Kvg)
                .pipe(
                    tap((output: EhcDocumentsOutput) => {
                        this.ehc.ehcPractice.ehcPracticeRecordDocuments = output.ehcDocuments;
                    })
                )
                .pipe(map((output: EhcDocumentsOutput) => output.ehcDocuments));
        }
        return of(this.ehc.ehcPractice.ehcPracticeRecordDocuments);
    }

    getEhcPracticeRecordFile(fileId: number): Observable<GeneralFileDto> {
        return this.ehcPracticeService.getFile(this.caseId, EhcStatus.Practice, fileId);
    }

    uploadEhcPracticeRecordFiles(ehcPracticeRecordId: string, input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.ehcPracticeService.uploadFiles(ehcPracticeRecordId, input);
    }

    deleteEhcPracticeRecordFile(fileId: number): Observable<void> {
        return this.ehcPracticeService.deleteFile(this.caseId, EhcStatus.Practice, fileId);
    }

    getEhcElectronicPatientRecord(): Observable<EhcElectronicPatientRecordDto> {
        return of(this.ehc.ehcElectronicPatientRecord).pipe(delay(0));
    }

    createOrUpdateEhcElectronicPatientRecord(input: CreateOrUpdateEhcElectronicPatientRecordInput): Observable<void> {
        this.applicationService.setStepToReview(EhcStatus.ElectronicPatientRecord);
        return this.ehcElectronicPatientRecordService.createOrUpdateEhcElectronicPatientRecord(input).pipe(tap((status: EhcStatus) => {
            this.ehc.ehcElectronicPatientRecord = input.ehcElectronicPatientRecord;
            this.ehc.currentStep = status;
        })).pipe(map((status: EhcStatus) => null));
    }

    getEhcStaff(): Observable<EhcStaffDto> {
        return of(this.ehc.ehcStaff).pipe(delay(0));
    }

    createOrUpdateEhcStaff(input: CreateOrUpdateEhcStaffInput): Observable<void> {
        this.applicationService.setStepToReview(EhcStatus.Staff);
        return this.ehcStaffService.createOrUpdateEhcStaff(input).pipe(tap((status: EhcStatus) => {
            this.ehc.ehcStaff = input.ehcStaff;
            this.ehc.currentStep = status;
        })).pipe(map((status: EhcStatus) => null));
    }

    getEhcFiles(step: EhcStatus, caseId = this.caseId): Observable<GeneralDocumentGroupDto[]> {
        return this.ehcDocumentService.getEhcDocuments(caseId, step).pipe(map((output: EhcDocumentsOutput) => output.ehcDocuments));
    }

    getEhcFile(step: EhcStatus, fileId: number): Observable<GeneralFileDto> {
        return this.ehcDocumentService.getFile(this.caseId, step, fileId);
    }

    uploadEhcFiles(step: EhcStatus, input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        input.step = step;
        return this.ehcDocumentService.uploadFiles(input);
    }

    deleteEhcFile(step: EhcStatus, fileId: number): Observable<void> {
        return this.ehcDocumentService.deleteFile(this.caseId, step, fileId);
    }

    getEhcContact(): Observable<EhcContactDto> {
        return of(this.ehc.ehcContact).pipe(delay(0));
    }

    createOrUpdateEhcContact(input: CreateOrUpdateEhcContactInput): Observable<void> {
        this.applicationService.setStepToReview(EhcStatus.Contact);
        return this.ehcContactService.createOrUpdateEhcContact(input).pipe(tap((status: EhcStatus) => {
            this.ehc.ehcContact = input.ehcContact;
            this.ehc.currentStep = status;
        })).pipe(map((status: EhcStatus) => null));
    }

    proceedEhcContact(): Observable<void> {
        return this.ehcContactService.proceedEhcContact(this.caseId).pipe(tap((status: EhcStatus) => {
            this.ehc.currentStep = status;
        })).pipe(map((status: EhcStatus) => null));
    }

    getEmployments(): Observable<EhcEmploymentOutput> {
        return of(this.ehc.ehcEmployments).pipe(delay(0));
    }

    createOrUpdateEhcEmployments(input: CreateOrUpdateEhcEmploymentsInput): Observable<void> {
        this.applicationService.setStepToReview(EhcStatus.Employment);
        return this.ehcPostgraduateTitleService.createOrUpdateEhcEmployments(input).pipe(tap((output: CreateOrUpdateEhcPostgraduateTitlesOutput) => {
            this.ehc.ehcPostgraduateTitles = output.ehcPostgraduateTitles;
            this.ehc.currentStep = output.status;
        })).pipe(map((output: CreateOrUpdateEhcPostgraduateTitlesOutput) => null));
    }

    getLiaContactReadOnly(): Observable<LiaContactDto> {
        return this.ehcContactService.getLiaContactReadOnly(this.caseId);
    }

    getPersonContactReadOnly(): Observable<MySironaPersonContactDto> {
        return this.ehcContactService.getPersonContactReadOnly(this.caseId);
    }

    hasEmployment(): boolean {
        let hasEmployment = false;
        for (let j = 0; j < this.ehc.ehcPostgraduateTitles?.length; j++) {
            hasEmployment = hasEmployment || this.ehc.ehcPostgraduateTitles[j].ehcEmployment?.length > 0;
        }
        return hasEmployment;
    }

    getEhcInvoiceAddress(): Observable<EhcInvoiceAddressDto> {
        return of(this.ehc.ehcInvoiceAddress).pipe(delay(0));
    }

    ehcInvoiceAddressOk(): Observable<void> {
        this.applicationService.setStepToReview(EhcStatus.InvoiceAddress);
        return this.ehcInvoiceAddressService.ehcInvoiceAddressOk(this.caseId).pipe(tap((status: EhcStatus) => {
            this.ehc.currentStep = status;
        })).pipe(map((status: EhcStatus) => null));
    }

    getInvoiceAddressDetail(): Observable<EhcInvoiceAddressDetail> {
        let firstName = this.ehc.ehcInvoiceAddress?.firstName;
        let lastName = this.ehc.ehcInvoiceAddress?.lastName;
        let fullName = this.ehc.ehcContact.titleId !== null ? this.l(this.ehc.ehcContact.titleId) + ' ' : '';
        if (!!firstName && !!lastName) {
            fullName += firstName + ' ' + lastName;
        } else {
            fullName += this.ehc.application.name;
        }
        let detail;
        if (this.ehc.ehcContact.countryId === AppConsts.codes.country.ch) {
            detail = new EhcInvoiceAddressDetail({
                name: fullName,
                street: this.ehc.ehcContact.street,
                streetNr: this.ehc.ehcContact.streetNr,
                postCode: this.ehc.ehcContact.postCode,
                city: this.ehc.ehcContact.city,
                email: this.ehc.ehcContact.email,
                countryId: this.ehc.ehcContact.countryId,
                postOfficeBox: this.ehc.ehcContact.postOfficeBox,
            });
        } else {
            detail = new EhcInvoiceAddressDetail({
                name: this.ehc.ehcContact.titleAddressSwiss,
                street: this.ehc.ehcContact.streetSwiss,
                streetNr: this.ehc.ehcContact.streetNrSwiss,
                postCode: this.ehc.ehcContact.postCodeSwiss,
                city: this.ehc.ehcContact.citySwiss,
                email: this.ehc.ehcContact.email,
                countryId: AppConsts.codes.country.ch,
                postOfficeBox: this.ehc.ehcContact.postOfficeBoxSwiss,
            });
        }
        return of(detail).pipe(delay(0));
    }

    createAddressDropdownList(): SelectItem[] {

        let addresses: SelectItem[] = [];

        const allNull = Object.values(this.ehc.ehcContact).every(value =>  {
        if (value === null) {
            return true;
        }
        });

        if (!allNull) {
            addresses.push({ value: AppConsts.codes.address.private, label: this.l('Address-Private') + ': ' + this.getPrivateAddressDescription(this.ehc.ehcContact) });
        }

        // for (let j = 0; j < this.ehc.ehcPostgraduateTitles?.length; j++) {
        //     for (let i = 0; i < this.ehc.ehcPostgraduateTitles[j].ehcEmployment?.length; i++) {
        //         // Use employment id as item value
        //         addresses.push({ value: this.ehc.ehcPostgraduateTitles[j].ehcEmployment[i].id, label: this.l('EmploymentSingular') + ': ' + this.getEmploymentAddressDescription(this.ehc.ehcPostgraduateTitles[j].ehcEmployment[i]) });
        //     }
        // }

        // if (addresses.length > 0) {
        //     addresses.push({ value: AppConsts.codes.address.invoice, label: this.l(AppConsts.codes.address.invoice) });
        // }

        return addresses;
    }

    private getPrivateAddressDescription(contact: EhcContactDto) {
        if (!contact) {
            return '';
        }

        let str = contact.street ?? '';

        if (contact.streetNr?.length > 0) {
            str =  str.length > 0 ? str.concat(' ', contact.streetNr) : str.concat(contact.streetNr);
        }

        if (contact.postCode?.length > 0) {
            str = str.length > 0 ? str.concat(', ', contact.postCode) : str.concat(contact.postCode);
        }

        if (contact.city?.length > 0) {
            str = str.length > 0 ? str.concat(' ', contact.city) : str.concat(contact.city);
        }

        return str;
    }

    private getEmploymentAddressDescription(employment: EhcEmploymentDto) {
        if (employment == null) {
            return '';
        }

        let str = employment.businessName ?? '';

        if (employment.street?.length > 0) {
            str =  str.length > 0 ? str.concat(', ', employment.street) : str.concat(employment.street);
        }

        if (employment.streetNr?.length > 0) {
            str =  str.length > 0 ? str.concat(' ', employment.streetNr) : str.concat(employment.streetNr);
        }

        if (employment.postCode?.length > 0) {
            str = str.length > 0 ? str.concat(', ', employment.postCode) : str.concat(employment.postCode);
        }

        if (employment.city?.length > 0) {
            str = str.length > 0 ? str.concat(' ', employment.city) : str.concat(employment.city);
        }

        return str;
    }

    createOrUpdateEhcInvoiceAddress(input: CreateOrUpdateEhcInvoiceAddressInput): Observable<void> {
        this.applicationService.setStepToReview(EhcStatus.InvoiceAddress);
        return this.ehcInvoiceAddressService.createOrUpdateEhcInvoiceAddress(input).pipe(tap((status: EhcStatus) => {
            this.ehc.ehcInvoiceAddress = input.ehcInvoiceAddress;
            this.ehc.currentStep = status;
        }))
            .pipe(map((status: EhcStatus) => null));
    }

    releaseEhc(comment: string): Observable<void> {

        if (comment === null) {
            comment = undefined;
        }

        return this.ehcService.releaseEhc(this.caseId, comment);
    }

    withdrawnCombinedApplication(caseId: string, withdrawLia: boolean, releaseLia: boolean): Observable<void> {
        return this.ehcService.withdrawCombinedApplications(caseId, withdrawLia, releaseLia);
    }

    releaseCombinedApplications(caseId: string, comment: string): Observable<void> {
        return this.ehcService.releaseCombinedApplications(caseId, comment);
    }

    getStepUrl(nextStep: EhcStatus): string {
        return this.applicationService.getNextStep(EhcService.getUrlForStep(nextStep));
    }

    isInEhcStatus(expectedStatus: EhcStatus): boolean {
        return this.ehc.currentStep === expectedStatus;
    }

    withdrawEhc(): Observable<void> {
        return this.ehcService.withdrawEhc(this.caseId);
    }

    static getUrlForStep(step: EhcStatus): string {
        switch (step) {
            case EhcStatus.SelfEmployment:
                return 'self-employment';
            case EhcStatus.Register:
                return 'register';
            case EhcStatus.MedicalField:
                return 'medical-field';
            case EhcStatus.LanguageSkill:
                return 'language-skills';
            case EhcStatus.PostgraduateTitle:
                return 'postgraduate-title';
            case EhcStatus.Practice:
                return 'practice';
            case EhcStatus.ElectronicPatientRecord:
                return 'electronic-patient-record';
            case EhcStatus.QualityRequirement:
                return 'quality-requirement';
            case EhcStatus.Staff:
                return 'staff';
            case EhcStatus.Contact:
                return 'contact';
            case EhcStatus.Employment:
                return 'employment';
            case EhcStatus.InvoiceAddress:
                return 'invoice-address';
            case EhcStatus.EmploymentOrPostgraduateTitle:
                return 'employment-or-postgraduate-title';
            case EhcStatus.Release:
            case EhcStatus.Released:
                return 'release';
            case EhcStatus.FinishReview:
                return 'finish-review';
            default:
                console.error('Invalid status for getUrlForStep');
                return null;
        }
    }

    getEhcRegister(): Observable<EhcRegisterDto> {
        return of(this.ehc.ehcRegister).pipe(delay(1));
    }

    getLegitimatedGlnForRegister(): Observable<string> {
        return this.ehcRegisterService.getLegitimatedGlnForRegister(this.caseId);
    }

    createOrUpdateEhcRegister(input: CreateOrUpdateEhcRegister): Observable<CreateOrUpdateEhcRegisterOutputDto> {
        this.applicationService.setStepToReview(EhcStatus.Register);
        return this.ehcRegisterService.createOrUpdateEhcRegister(input).pipe(
            tap((output: CreateOrUpdateEhcRegisterOutputDto) => {
                this.ehc.ehcRegister = output.ehcRegister;
            })
        );
    }

    acceptRegisterCheck(): Observable<EhcStatus> {
        this.applicationService.setStepToReview(EhcStatus.Register);
        return this.ehcRegisterService.acceptRegisterCheck(this.caseId).pipe(
            tap((status: EhcStatus) => {
                this.ehc.currentStep = status;
            })
        );
    }

    setEhcRegisterStepToReview() {
        this.applicationService.setStepToReview(EhcStatus.Register);
    }

    createOrUpdateEhcQualityRequirement(input: CreateOrUpdateEhcQualityRequirementInput): Observable<void> {
        this.applicationService.setStepToReview(EhcStatus.QualityRequirement);
        return this.ehcQualityRequirementService
            .createOrUpdateEhcQualityRequirement(input)
            .pipe(
                tap((status: EhcStatus) => {
                    this.ehc.ehcQualityRequirement = input.ehcQualityRequirement;
                    this.ehc.currentStep = status;
                })
            )
            .pipe(map((status: EhcStatus) => null));
    }

    getEhcQualityRequirement(): Observable<EhcQualityRequirementDto> {
        return of(this.ehc.ehcQualityRequirement).pipe(delay(0));
    }

    getEhcEmploymentOrPostgraduateTitle(): Observable<EhcEmploymentOrPostgraduateTitleDto[]> {
        return of(this.ehc.ehcEmploymentOrPostgraduateTitles).pipe(delay(0));
    }

    getEhcEmploymentOrPostgraduateTitleInitialList(): Observable<EhcEmploymentOrPostgraduateTitleDto[]> {
        return this.ehcEmploymentOrPostgraduateTitleService.getInitialList(this.caseId);
    }

    createOrUpdateEhcEmploymentOrPostgraduateTitles(input: CreateOrUpdateEhcEmploymentOrPostgraduateTitleInput): Observable<void> {
        this.applicationService.setStepToReview(EhcStatus.EmploymentOrPostgraduateTitle);
        return this.ehcEmploymentOrPostgraduateTitleService.createOrUpdateEhcEmploymentOrPostgraduateTitles(input).pipe(tap((output: CreateOrUpdateEhcEmploymentOrPostgraduateTitlesOutput) => {
            this.ehc.ehcEmploymentOrPostgraduateTitles = input.ehcEmploymentOrPostgraduateTitles;
            this.ehc.currentStep = output.status;
        })).pipe(map(() => null));
    }

    getEhcByLiaId(liaId: number): Observable<string> {
        return this.ehcService.getEhcByLiaId(liaId);
    }

    getEhcByLiaIdOrNull(liaId: number): Observable<string> {
        return this.ehcService.getEhcByLiaIdOrNull(liaId);
    }

    hasEmploymentOrPostgraduateTitle(): Observable<boolean> {
        return this.ehcPracticeService.hasEmploymentOrPostgraduateTitle(this.caseId);
    }

    getFirstStep(): Observable<EhcStatus> {
        return this.ehcService.getFirstStep(this.caseId).pipe();
    }

    getElectronicPatientRecordFiles(forceRefresh?: boolean, caseId = this.caseId): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.ehcElectronicPatientRecordService
                .getEhcElectronicPatientRecordDocuments(caseId, EhcStatus.ElectronicPatientRecord)
                .pipe(
                    tap((output: EhcDocumentsOutput) => {
                        this.ehc.ehcElectronicPatientRecord.electronicPatientRecordDocuments = output.ehcDocuments;
                    })
                )
                .pipe(map((output: EhcDocumentsOutput) => output.ehcDocuments));
        }
        return of(this.ehc.ehcElectronicPatientRecord.electronicPatientRecordDocuments);
    }

    getElectronicPatientRecordFile(fileId: number): Observable<GeneralFileDto> {
        return this.ehcElectronicPatientRecordService.getFile(this.caseId, EhcStatus.ElectronicPatientRecord, fileId);
    }

    uploadElectronicPatientRecordFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.ehcElectronicPatientRecordService.uploadFiles(input);
    }

    deleteElectronicPatientRecordFile(fileId: number): Observable<void> {
        return this.ehcElectronicPatientRecordService.deleteFile(this.caseId, EhcStatus.ElectronicPatientRecord, fileId);
    }

    getQualityRequirementFiles(forceRefresh?: boolean, caseId = this.caseId): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.ehcQualityRequirementService
                .getEhcQualityRequirementDocuments(caseId, EhcStatus.QualityRequirement)
                .pipe(
                    tap((output: EhcDocumentsOutput) => {
                        this.ehc.ehcQualityRequirement.qualityRequirementDocuments = output.ehcDocuments;
                    })
                )
                .pipe(map((output: EhcDocumentsOutput) => output.ehcDocuments));
        }
        return of(this.ehc.ehcQualityRequirement.qualityRequirementDocuments);
    }

    getQualityRequirementFile(fileId: number): Observable<GeneralFileDto> {
        return this.ehcQualityRequirementService.getFile(this.caseId, EhcStatus.QualityRequirement, fileId);
    }

    uploadQualityRequirementFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.ehcQualityRequirementService.uploadFiles(input);
    }

    deleteQualityRequirementFile(fileId: number): Observable<void> {
        return this.ehcQualityRequirementService.deleteFile(this.caseId, EhcStatus.QualityRequirement, fileId);
    }
}

export class EhcInvoiceAddressDetail {
    name: string;
    street: string;
    streetNr: string;
    postCode: string;
    city: string;
    email: string;
    countryId: string;
    postOfficeBox: string;

    constructor (data: EhcInvoiceAddressDetail) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property)) {
                    (<any>this)[property] = (<any>data)[property];
                }
            }
        }
    }
}
